import {useCallback} from 'react';
import SegmentUtils from '@spothero/utils/segment';
import {useAppSelector} from 'hooks/redux';

export interface TrackEventParams {
    event: string;
    properties?: Record<string, unknown>;
}

export interface TrackPageParams {
    name: string;
    properties?: Record<string, unknown>;
}

/**
 * Hook that handles Segment event tracking
 * @function useSegment
 * @returns Exposes the segment tracking functions
 *
 * @example
 * const {trackEvent, trackPage} = useSegment();
 *
 * trackEvent({
 *     event: 'event-name',
 *     properties: {foo: 'bar'}
 * });
 *
 * trackPage({
 *     name: 'page-name',
 *     properties: {foo: 'bar'}
 * });
 */
const useSegment = (): {
    trackEvent: (params: TrackEventParams) => void;
    trackPage: (params: TrackPageParams) => void;
} => {
    const {segment} = useAppSelector((state) => state.user);
    const isUser = segment?.email?.length;

    const trackEvent = useCallback(
        ({event, properties}: TrackEventParams) => {
            if (!event || !isUser) {
                return;
            }

            SegmentUtils.track({
                event,
                properties: {
                    ...properties,
                    user: segment,
                },
            });
        },
        [isUser, segment]
    );

    const trackPage = useCallback(
        ({name, properties}: TrackPageParams) => {
            if (!name || !isUser) {
                return;
            }

            SegmentUtils.page({
                name,
                properties: {
                    ...properties,
                    user: segment,
                },
            });
        },
        [isUser, segment]
    );

    return {trackEvent, trackPage};
};

export default useSegment;
